/**
 * Getting the local screen share to the SFU, and keeping it there.
 *
 * The share lives longer than the transport that carries it, and that is what
 * made it intermittent. It was published once, from an effect keyed on the
 * stream alone:
 *
 *  - Shared before the send transport existed (just after entering the
 *    meeting), the publish returned early and nothing ever tried again. The
 *    presenter saw their own screen — the preview is the local capture — while
 *    nobody else received anything.
 *  - When the session was rebuilt under a running share (ICE failure, network
 *    change, `sessionLost`), the producers died with the old transport and were
 *    never published again; the list still held them, so even a fresh publish
 *    was refused as a double.
 *  - Stopped while `produce()` was still awaiting, the stop found nothing in
 *    the list, and the producer that arrived a moment later stayed live — an
 *    orphan, frozen for viewers, still listed, and blocking the next share.
 *
 * So the publisher holds the share (`start`/`stop`) and the transport
 * (`setTransport`) separately and publishes whenever it has both. Every share
 * is a generation: a produce that resolves for a generation or transport that
 * is no longer current closes what it made instead of keeping it.
 */

export interface ProducerLike {
  id: string
  close(): void
  kind?: string
  paused?: boolean
  pause?(): void
  resume?(): void
}

export interface TransportLike {
  produce(options: { track: MediaStreamTrack } & Record<string, unknown>): Promise<ProducerLike>
}

type Deps<P extends ProducerLike, T extends TransportLike> = {
  /** Produce one track of the share on `transport`. */
  produce: (transport: T, track: MediaStreamTrack) => Promise<P>
  /** A producer is live on the current transport. */
  onProduced: (producer: P) => void
  /**
   * A producer is finished with: close it here and on the server, and tell
   * the room. `serverAlive` is false for producers of a transport that is
   * already gone — the server dropped those with the session.
   */
  onClosed: (producer: P, serverAlive: boolean) => void
  /** The share reached the room — once per share, not once per republish. */
  announceStarted: () => void
  /** The share ended. Carries exactly the producers it ended. */
  announceStopped: (producerIds: string[]) => void
  log?: (message: string, data?: Record<string, unknown>) => void
}

export class ScreenSharePublisher<P extends ProducerLike = ProducerLike, T extends TransportLike = TransportLike> {
  private stream: MediaStream | null = null
  private transport: T | null = null
  private producers: P[] = []
  private generation = 0
  /** Bumped with every transport, so a publish is keyed to the one it runs on. */
  private transportEpoch = 0
  /** `${generation}:${transportEpoch}` of the publish in flight, so overlapping calls coalesce. */
  private inFlight: string | null = null
  private announcedFor = -1
  /** The share's sound is switched off. */
  private audioMuted = false

  private readonly deps: Deps<P, T>

  constructor(deps: Deps<P, T>) {
    this.deps = deps
  }

  /** The share the user started. Publishes now, or as soon as a transport exists. */
  start(stream: MediaStream): Promise<void> {
    if (this.stream === stream) return this.publish()
    if (this.stream) this.stop()
    this.generation++
    this.stream = stream
    return this.publish()
  }

  /**
   * Switch the share's sound off or on — the meeting's share-audio switch
   * (services/share-audio.ts), the Screen Share page's tab sound.
   *
   * Muted, the share's audio is silenced; unmuted, it plays again. The picture
   * carries on either way, and the state holds across republishes, so a share
   * published while muted goes out silent. Local only: whoever hosts this
   * mirrors it to the SFU (producer-pause-sync), which is what viewers read.
   */
  setAudioMuted(muted: boolean): void {
    this.audioMuted = muted
    for (const p of this.producers) {
      if (p.kind !== 'audio' || p.paused === muted) continue
      if (muted) p.pause?.()
      else p.resume?.()
    }
  }

  /** The share ended: close what it produced and tell the room which producers went. */
  stop(): void {
    const ended = this.producers
    const hadShare = this.stream !== null
    this.generation++
    this.stream = null
    this.producers = []
    for (const p of ended) this.deps.onClosed(p, true)
    if (hadShare || ended.length) this.deps.announceStopped(ended.map((p) => p.id))
  }

  /**
   * The send transport, as it is now. A different one means the session was
   * rebuilt: the old producers went with it, so they are dropped and the share
   * — if one is running — is published again on the new one.
   */
  setTransport(transport: T | null): void {
    if (transport === this.transport) return
    const stale = this.producers
    this.transport = transport
    this.transportEpoch++
    this.producers = []
    for (const p of stale) this.deps.onClosed(p, false)
    if (stale.length) this.deps.log?.('screen share: transport replaced, republishing', { dropped: stale.length })
    if (this.stream) void this.publish()
  }

  /** What is live, for tests and diagnostics. */
  get producerIds(): string[] {
    return this.producers.map((p) => p.id)
  }

  /** The share's live audio producers, for a server-side pause mirror that starts after they did. */
  get audioProducers(): P[] {
    return this.producers.filter((p) => p.kind === 'audio')
  }

  /** A share is running and the room is receiving it. */
  get live(): boolean {
    return this.stream !== null && this.producers.length > 0
  }

  /** A share is running but nobody is receiving it yet. */
  get pending(): boolean {
    return this.stream !== null && this.producers.length === 0
  }

  private async publish(): Promise<void> {
    const stream = this.stream
    const transport = this.transport
    const generation = this.generation
    if (!stream) return
    if (!transport) {
      this.deps.log?.('screen share: waiting for the send transport')
      return
    }
    if (this.producers.length > 0) return
    const key = `${generation}:${this.transportEpoch}`
    if (this.inFlight === key) return
    this.inFlight = key

    try {
      // Video first, and each track on its own: tab audio failing to produce
      // must not take the picture down with it.
      const tracks = [...stream.getVideoTracks(), ...stream.getAudioTracks()].filter(
        (t) => t.readyState !== 'ended',
      )
      for (const track of tracks) {
        let producer: P
        try {
          producer = await this.deps.produce(transport, track)
        } catch (err) {
          this.deps.log?.('screen share: produce failed', { kind: track.kind, error: (err as Error)?.message })
          continue
        }
        // Stopped, restarted or moved to a new transport while that was in
        // flight: this producer belongs to nobody, so it is closed, not kept.
        if (generation !== this.generation || transport !== this.transport) {
          this.deps.log?.('screen share: produce finished for a share that is gone — closing it', { kind: track.kind })
          this.deps.onClosed(producer, transport === this.transport)
          continue
        }
        if (this.audioMuted && producer.kind === 'audio') producer.pause?.()
        this.producers.push(producer)
        this.deps.onProduced(producer)
      }
    } finally {
      if (this.inFlight === key) this.inFlight = null
    }

    if (generation === this.generation && this.producers.length > 0 && this.announcedFor !== generation) {
      this.announcedFor = generation
      this.deps.announceStarted()
    }
  }
}
